import { isNumber } from 'lodash';
import { Coordinate } from '../../models/coordinate';
import Circle = google.maps.Circle;

export const CENTER_OF_EUROPE = {
  lat: 54.526,
  lng: 15.255,
};

export const MAX_ZOOM = 21;

export const MARKER_DIMENSIONS = { w: 40, h: 56 };
export const INFO_WINDOW_DIMENSIONS = { w: 208, h: 101, padding: 10 };
export const MARKER_TOOLTIP_DIMENSIONS = { w: 100, h: 24, padding: 4 };

export type MousePositionType = { x: number; y: number };

export enum GxMVCobject {
  'INFO_WINDOW',
  'TOOLTIP',
  'MARKER'
}

export interface GxMarker extends google.maps.Marker {
  meta?: {
    // for easy CSS selector access in `document.querySelector`
    id?: string;
    // for easy mouseover/mouseout highlighting thru icon switching
    icons?: {
      defaultIcon: google.maps.Icon | string,
      highlightedIcon: google.maps.Icon | string,
      minimizedIcon?: google.maps.Icon | string,

      // whether or not the icons will need to be reconstructed
      alreadyContainsSvgIcons?: boolean;
      // whether or not the default svg scaffolding should be used
      defaultToInternalGxMarkerSvg?: boolean;
    },
    positionOverride?: google.maps.LatLngLiteral,
    [key: string]: any // allow *any* custom metadata, if need be
  };
}

export interface MapArtefact<Marker = GxMarker> {
  parentLocation: {
    // we're only keeping the id (the reference)
    // to fetch the latest state from the store and/or CC feed
    id: string;
    marker?: Marker;
    radiusCircle?: Circle,
    hoverTooltip?: any,
    actionsPopup?: any; // TODO In progress
    eventListeners?: google.maps.MapsEventListener[],
    // in CV/Diff map if custom pins are minimized, there should be mouse over/out listeners here
    togglePinSizeEventListeners?: google.maps.MapsEventListener[]
  };

  // children locations are only used in CC map; ergo optional
  childrenLocations?: Array<{
    // we're only keeping the id (the reference)
    // to fetch the latest state from the store and/or CC feed
    safeId: string;
    marker?: Marker;
    hoverTooltip?: any;
    actionsPopup?: any; // TODO In progress
    eventListeners?: google.maps.MapsEventListener[]
  }>;
}

export const GOOGLE_MAPS_CONTROL_SIZE = 28;

export const GOOGLE_MAPS_PANORAMA_OPTIONS:
    Partial<google.maps.StreetViewPanoramaOptions>
    // for some reason, `controlSize` isn't supported in the map types
    & Pick<google.maps.MapOptions, 'controlSize'> = {
  visible: true,
  scrollwheel: false,

  pov: {
    heading: 0, // point north by default
    pitch: 0, // horizontal by default
  },

  controlSize: GOOGLE_MAPS_CONTROL_SIZE,

  clickToGo: true,
  addressControl: false, // this is too distracting
  enableCloseButton: false, // we'll handle closing on our own
  linksControl: false,
  fullscreenControl: false,
  motionTracking: false, // causes havoc on motion-sensitive devices, fixes BLS-4429
  motionTrackingControl: false,
};

export interface OnMapLoadingStateChangedEvent {
  map: google.maps.Map;
  isLoaded: boolean;
}

/**
 * Ensures parsable `latitude` and `longitude` object members.
 * @param coords
 */
export const areCoordsValid = (coords?: Coordinate) => {
  return typeof coords !== 'undefined'
      && isNumber(coords?.latitude)
      && isNumber(coords?.longitude);
};

/**
 * Coordinates comparison
 * @param c1
 * @param c2
 */
export const areCoordsEqual = (c1: Coordinate, c2: Coordinate) => {
  if (!areCoordsValid(c1) || !areCoordsValid(c2)) {
    return false;
  }
  return c1.latitude === c2.latitude && c1.longitude === c2.longitude;
};

/**
 * Conversion helper
 * @param position
 */
export const convertToCoordinateType = (position: google.maps.LatLngLiteral): Coordinate => {
  return {
    latitude: position?.lat,
    longitude: position?.lng,
  };
};


/**
 * Given a panorama center, calculate the heading (the camera heading in degrees relative to true north -- 0°)
 * and the pitch (relative to the street view vehicle) so that the streetview "looks"
 * in the direction of a given `targetPosition`.
 * @param panoramaBaselineCenter
 * @param targetPosition
 */
export const getOptimizedPanoramaView = (
    panoramaBaselineCenter: google.maps.LatLng,
    targetPosition: google.maps.LatLng,
): Pick<google.maps.StreetViewPanoramaOptions, 'pov' | 'position'> => {
  return {
    position: panoramaBaselineCenter,
    pov: {
      heading: google.maps.geometry.spherical.computeHeading(
          panoramaBaselineCenter,
          targetPosition,
      ),
      // -5° because the `targetPosition`'s pin tends to be "on the ground"
      // and so if we want to see it, we need to "look down" a bit.
      pitch: -5,
    },
  };
};